import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { getCmdkProviders, type CmdkProvider } from "./cmdk_registry.js";
import { useApi } from "./components.js";

interface ProviderItemsProps {
  provider: CmdkProvider;
  onClose: () => void;
}

function ProviderItems({ provider, onClose }: ProviderItemsProps) {
  const groups = provider.useItems();

  return (
    <>
      {groups.map(group => (
        <CommandGroup key={group.heading} heading={group.heading}>
          {group.items.map((ItemComponent, idx) => (
            <ItemComponent key={idx} onClose={onClose} />
          ))}
        </CommandGroup>
      ))}
    </>
  );
}

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const { getToken } = useAuth();
  const pool = useApi();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const dangerouslyDeleteOrgData = async () => {
    if (
      !confirm(
        "Are you sure you want to delete all org data? This cannot be undone.",
      )
    ) {
      return;
    }
    setOpen(false);
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) return;
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) return;
      await orgApi.dangerouslyDeleteOrgData();
      window.location.reload();
    } finally {
      pool.release();
    }
  };

  const providers = getCmdkProviders();

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {providers.map(provider => (
          <ProviderItems
            key={provider.type}
            provider={provider}
            onClose={() => setOpen(false)}
          />
        ))}
        <CommandGroup heading="Danger">
          <CommandItem
            value="delete org data"
            onSelect={dangerouslyDeleteOrgData}
          >
            Delete all org data
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
